import { Participant, Room } from 'livekit-client';
import { isElectron } from '@web-chat-core/ui-kit/helpers/electron';
import { selectRoomMetadata } from 'state/selectors';
import { roomInfoStore } from 'state/store';
import { getParticipantUserIdFromMetadata } from 'useCase/participant/useParticipantMetadata';
import {
    forceStopCameraForWebinarListener,
    forceStopMicForWebinarListener,
    stopScreenShareIfHardLocked,
} from 'useCase/webinar/webinarStatus';
import { isLocalWebinarInitiator, showWebinarModeSnackbar } from 'useCase/webinar/webinarModeSnackbar';
import type { ModerationCommand } from '../types';
import { MODERATOR_ACTIONS, type ModeratorAction } from '../constants';
import { findParticipantBySid } from '../utils/findParticipantBySid';
import { findParticipantByIdentity } from '../utils/findParticipantByIdentity';
import { findParticipantByUserId } from '../utils/findParticipantByUserId';
import { handleKickRedirect } from '../utils/handleKickRedirect';
import { parseModeratorsFromMetadata } from '../utils/parseModerators';
import { determineModeratorType } from '../utils/determineModeratorType';

const BROADCAST_ACTIONS = new Set<ModeratorAction>([
    MODERATOR_ACTIONS.START_WEBINAR,
    MODERATOR_ACTIONS.END_WEBINAR,
    MODERATOR_ACTIONS.WEBINAR_FORCE_MIC_OFF,
    MODERATOR_ACTIONS.WEBINAR_FORCE_CAM_OFF,
    MODERATOR_ACTIONS.WEBINAR_FORCE_SCREEN_SHARE_OFF,
]);

/** Команды, не требующие targetSid (broadcast всем). */
export const isBroadcastAction = (action: ModeratorAction): boolean => BROADCAST_ACTIONS.has(action);

export interface CommandHandlersContext {
    showSnackbar: (message: string) => void;
    getDisplayData: (participant: Participant, isLocal: boolean) => { name: string };
    setAudioEnabled: (enabled: boolean) => void;
    setVideoEnabled: (enabled: boolean) => void;
    t: (key: string, options?: Record<string, string | number>) => string;
    onLocalUserKicked?: () => void;
}

const isTargetLocal = (room: Room, cmd: ModerationCommand): boolean => {
    const local = room.localParticipant;
    if (cmd.targetIdentity != null && cmd.targetIdentity !== '') {
        return local.identity === cmd.targetIdentity;
    }
    if (cmd.targetSid != null && cmd.targetSid !== '') {
        return local.sid === cmd.targetSid;
    }
    return false;
};

const findTargetParticipant = (room: Room, cmd: ModerationCommand): Participant | null => {
    if (cmd.targetIdentity != null && cmd.targetIdentity !== '') {
        const byIdentity = findParticipantByIdentity(room, cmd.targetIdentity);
        if (byIdentity) return byIdentity;
    }
    if (cmd.targetSid != null && cmd.targetSid !== '') {
        return findParticipantBySid(room, cmd.targetSid);
    }
    return null;
};

/**
 * Находим модератора: сначала по identity, потом SID, потом по userId из метаданных
 */
const findModeratorParticipant = (room: Room, cmd: ModerationCommand): Participant | null => {
    if (cmd.moderatorIdentity != null && cmd.moderatorIdentity !== '') {
        const byIdentity = findParticipantByIdentity(room, cmd.moderatorIdentity);
        if (byIdentity) return byIdentity;
    }
    if (cmd.moderatorSid != null && cmd.moderatorSid !== '') {
        const bySid = findParticipantBySid(room, cmd.moderatorSid);
        if (bySid) return bySid;
    }
    const metadata = selectRoomMetadata();
    const moderators = parseModeratorsFromMetadata(metadata);
    for (const moderator of moderators) {
        const type = determineModeratorType(moderator);
        if (type === 'USER' && moderator.userId) {
            const participantByUserId = findParticipantByUserId(room, moderator.userId);
            if (participantByUserId) return participantByUserId;
        }
    }
    return null;
};

export const handleModerationCommand = (room: Room, cmd: ModerationCommand, context: CommandHandlersContext): void => {
    const isLocalTarget = isTargetLocal(room, cmd);

    const moderatorParticipant = findModeratorParticipant(room, cmd);
    const actorName = moderatorParticipant
        ? context.getDisplayData(moderatorParticipant, moderatorParticipant.isLocal).name
        : '';

    switch (cmd.action) {
        case MODERATOR_ACTIONS.MUTE:
            if (isLocalTarget) {
                const wasMicOn = room.localParticipant.isMicrophoneEnabled;
                room.localParticipant.setMicrophoneEnabled(false);
                context.setAudioEnabled(false);
                if (wasMicOn) {
                    const message = context.t('web_videoconference_moderator_snack_moderator_muted_you', {
                        '0': actorName,
                    });
                    context.showSnackbar(message);
                }
            }
            break;

        case MODERATOR_ACTIONS.DISABLE_VIDEO:
            if (isLocalTarget) {
                const wasCameraOn = room.localParticipant.isCameraEnabled;
                room.localParticipant.setCameraEnabled(false);
                context.setVideoEnabled(false);
                if (wasCameraOn) {
                    const message = context.t('web_videoconference_moderator_snack_stopped_video_you', {
                        '0': actorName,
                    });
                    context.showSnackbar(message);
                }
            }
            break;

        case MODERATOR_ACTIONS.MUTE_ALL_EXCEPT:
            if (!isLocalTarget) {
                const wasMicOn = room.localParticipant.isMicrophoneEnabled;
                room.localParticipant.setMicrophoneEnabled(false);
                context.setAudioEnabled(false);
                if (wasMicOn) {
                    const message = context.t('web_videoconference_moderator_snack_moderator_muted_you', {
                        '0': actorName,
                    });
                    context.showSnackbar(message);
                }
            }
            break;

        case MODERATOR_ACTIONS.DISABLE_VIDEO_ALL_EXCEPT:
            if (!isLocalTarget) {
                const wasCameraOn = room.localParticipant.isCameraEnabled;
                room.localParticipant.setCameraEnabled(false);
                context.setVideoEnabled(false);
                if (wasCameraOn) {
                    const message = context.t('web_videoconference_moderator_snack_stopped_video_you', {
                        '0': actorName,
                    });
                    context.showSnackbar(message);
                }
            }
            break;

        case MODERATOR_ACTIONS.REMOVE_FROM_CALL:
            if (isLocalTarget) {
                context.onLocalUserKicked?.();
                room.disconnect();

                // в десктопе не делаем редирект на prejoin
                if (!isElectron) handleKickRedirect();
            } else {
                let targetName = context.t('web_call_unnamed_user');
                const targetParticipant = findTargetParticipant(room, cmd);
                if (targetParticipant) {
                    targetName = context.getDisplayData(targetParticipant, false).name;
                }
                const message = context.t('web_videoconference_moderator_snack_removed_user', {
                    '0': targetName,
                });
                context.showSnackbar(message);
            }
            break;

        case MODERATOR_ACTIONS.INVITE_TO_SPEAK:
            if (isLocalTarget) {
                const message = context.t('moderator_invite_to_speak_notification', {
                    '0': actorName,
                });
                console.log('[Moderation] INVITE_TO_SPEAK: показываем снекбар участнику', { actorName, message });
                context.showSnackbar(message);
            }
            break;

        case MODERATOR_ACTIONS.MAKE_MODERATOR:
            if (isLocalTarget) {
                const message = context.t('web_videoconference_moderator_snack_made_moderator', {
                    '0': actorName,
                });
                context.showSnackbar(message);
                const userId = getParticipantUserIdFromMetadata(room.localParticipant.metadata);
                const identity = room.localParticipant.identity ?? null;
                if (userId || identity) {
                    roomInfoStore.addModeratorToMetadata(userId, identity);
                }
            }
            break;

        case MODERATOR_ACTIONS.REVOKE_MODERATOR:
            if (isLocalTarget) {
                const message = context.t('moderator_revoked_your_moderator_rights', {
                    '0': actorName,
                });
                context.showSnackbar(message);
                const userId = getParticipantUserIdFromMetadata(room.localParticipant.metadata);
                const identity = room.localParticipant.identity ?? null;
                if (userId || identity) {
                    roomInfoStore.removeModeratorFromMetadata(userId, identity);
                }
            }
            break;

        case MODERATOR_ACTIONS.START_WEBINAR:
        case MODERATOR_ACTIONS.END_WEBINAR: {
            if (isLocalWebinarInitiator(room, cmd)) break;

            const enabled = cmd.action === MODERATOR_ACTIONS.START_WEBINAR;
            const name = actorName || context.t('web_call_unnamed_user');
            showWebinarModeSnackbar(enabled, name);
            break;
        }

        case MODERATOR_ACTIONS.MAKE_SPEAKER:
            if (isLocalTarget) {
                context.showSnackbar(context.t('vks_webinar_user_made_speaker_tooltip', { '0': actorName }));
                // Оптимистично добавляем себя в спикеры, чтобы снять жёсткую блокировку немедленно
                const assignUserId = getParticipantUserIdFromMetadata(room.localParticipant.metadata);
                const assignIdentity = room.localParticipant.identity ?? null;
                if (assignUserId) {
                    roomInfoStore.addSpeakerToWebinarMetadata({ userId: assignUserId, identity: null });
                } else if (assignIdentity) {
                    roomInfoStore.addSpeakerToWebinarMetadata({ identity: assignIdentity });
                }
            }
            break;

        case MODERATOR_ACTIONS.REVOKE_SPEAKER:
            if (isLocalTarget) {
                context.showSnackbar(context.t('vks_webinar_speaker_rights_revoke', { '0': actorName }));
                // Читаем webinarMode ДО оптимистичного обновления, пока metadata точно актуальна
                const revokeWebinarMode = selectRoomMetadata()?.webinarMode;
                const revokeUserId = getParticipantUserIdFromMetadata(room.localParticipant.metadata);
                const revokeIdentity = room.localParticipant.identity ?? null;
                if (revokeUserId || revokeIdentity) {
                    roomInfoStore.removeSpeakerFromWebinarMetadata({
                        userId: revokeUserId,
                        identity: revokeIdentity,
                    });
                }
                // Принудительно выключаем медиа (без guard по isMicrophoneEnabled — трек и контекст могут расходиться)
                if (revokeWebinarMode?.microDisabled) {
                    room.localParticipant.setMicrophoneEnabled(false);
                    context.setAudioEnabled(false);
                }
                if (revokeWebinarMode?.cameraDisabled) {
                    room.localParticipant.setCameraEnabled(false);
                    context.setVideoEnabled(false);
                }
                if (revokeWebinarMode?.screenShareDisabled) {
                    // После removeSpeakerFromWebinarMetadata локальный участник — слушатель, guard как в UI
                    stopScreenShareIfHardLocked(room.localParticipant);
                }
            }
            break;

        case MODERATOR_ACTIONS.WEBINAR_FORCE_MIC_OFF:
            forceStopMicForWebinarListener(room.localParticipant, context.setAudioEnabled);
            break;

        case MODERATOR_ACTIONS.WEBINAR_FORCE_CAM_OFF:
            forceStopCameraForWebinarListener(room.localParticipant, context.setVideoEnabled);
            break;

        case MODERATOR_ACTIONS.WEBINAR_FORCE_SCREEN_SHARE_OFF:
            stopScreenShareIfHardLocked(room.localParticipant);
            break;

        default:
            console.warn('[Moderation] неизвестное действие', { action: cmd.action });
    }
};
